export interface ProductDetail {
  id: string;
  eyebrow: string;
  title: string;
  tagline: string;
  subtitle: string;
  description: string[];
  specs: { label: string; value: string }[];
  benefits: string[];
  recommendedFor: string;
  image: string;
}

export const productDetails: Record<string, ProductDetail> = {
  'pig-starter': {
    id: 'pig-starter', eyebrow: 'Pig Feed', title: 'Pig Starter', tagline: 'Strong Starts. Efficient Growth.', subtitle: 'Complete feed for young pigs',
    description: ['ZFAST Pig Starter is a stage-specific complete feed for young pigs, supporting uniform growth, digestion, immunity, bone development, and feed conversion.'],
    specs: [{ label: 'Pack size', value: '25 kg' }, { label: 'Feed stage', value: 'Starter' }],
    benefits: ['Supports fast and uniform growth', 'Supports strong immunity', 'Supports gut health and bone development', 'Encourages feed conversion and palatability'],
    recommendedFor: 'Young pigs in the starter stage; follow the label for stage and feeding directions.', image: '/images/products/pig-feed/zfast-starter.webp'
  },
  'pig-grower': {
    id: 'pig-grower', eyebrow: 'Pig Feed', title: 'Pig Grower', tagline: 'Steady Growth. Strong Condition.', subtitle: 'Complete feed for growing pigs',
    description: ['ZFAST Pig Grower is formulated for the grower stage, supporting consistent body growth, feed conversion, immunity, body condition, and healthy digestion.'],
    specs: [{ label: 'Pack size', value: '25 kg' }, { label: 'Feed stage', value: 'Grower' }],
    benefits: ['Supports optimal growth', 'Encourages feed conversion', 'Supports strong immunity', 'Supports body condition and digestion'],
    recommendedFor: 'Growing pigs in stage-wise commercial feeding programmes.', image: '/images/products/pig-feed/zfast-grower.webp'
  },
  'pig-finisher': {
    id: 'pig-finisher', eyebrow: 'Pig Feed', title: 'Pig Finisher', tagline: 'Better Finish. Better Returns.', subtitle: 'Complete feed for finishing pigs',
    description: ['ZFAST Pig Finisher supports the final growth stage with nutrition positioned for weight gain, feed conversion, meat quality, immunity, and healthy digestion.'],
    specs: [{ label: 'Pack size', value: '25 kg' }, { label: 'Feed stage', value: 'Finisher' }],
    benefits: ['Supports maximum weight gain', 'Encourages better feed conversion', 'Supports meat quality and immunity', 'Supports gut health and farm returns'],
    recommendedFor: 'Finishing pigs progressing toward market weight.', image: '/images/products/pig-feed/zfast-finisher.webp'
  },
  'sow-gestation': {
    id: 'sow-gestation', eyebrow: 'Pig Feed', title: 'Sow Gestation', tagline: 'Balanced Support for Breeder Sows.', subtitle: 'Complete feed for gestating sows',
    description: ['ZFAST Sow Gestation Feed is developed for breeder-stage nutrition, supporting body condition, foetal development, immunity, mammary development, and reproductive performance.'],
    specs: [{ label: 'Pack size', value: '50 kg' }, { label: 'Feed stage', value: 'Gestation' }],
    benefits: ['Supports optimal body condition', 'Supports foetal and mammary development', 'Supports strong immunity', 'Supports reproductive performance'],
    recommendedFor: 'Gestating sows under a professionally managed breeder feeding programme.', image: '/images/products/pig-feed/sow-gestation.webp'
  },
  'sow-lactation': {
    id: 'sow-lactation', eyebrow: 'Pig Feed', title: 'Sow Lactation', tagline: 'Milk Support. Healthy Litters.', subtitle: 'Complete feed for lactating sows',
    description: ['ZFAST Sow Lactation Feed is designed to support milk yield and quality, sow immunity and body condition, premium nutrition, and healthy litter growth.'],
    specs: [{ label: 'Pack size', value: '50 kg' }, { label: 'Feed stage', value: 'Lactation' }],
    benefits: ['Supports milk production and quality', 'Supports strong immunity', 'Helps maintain sow body condition', 'Supports healthy litter growth'],
    recommendedFor: 'Lactating sows under a professionally managed breeder feeding programme.', image: '/images/products/pig-feed/sow-lactation.webp'
  },
  'z-8000': {
    id: 'z-8000',
    eyebrow: 'Cattle Feed',
    title: 'ZFAST 8000',
    tagline: 'Reliable Nutrition. Everyday Performance.',
    subtitle: 'Reliable Nutrition for Everyday Performance',
    description: [
      'ZFAST 8000 is a balanced cattle feed formulated to provide consistent nutrition for dairy and growing animals. It helps maintain body condition, supports healthy growth, and promotes overall herd performance at an economical cost.'
    ],
    specs: [
      { label: 'Crude Protein', value: '23%' },
      { label: 'Crude Fat', value: '5.0%' },
      { label: 'Crude Fiber', value: '7.0%' }
    ],
    benefits: [
      'Supports healthy growth and development',
      'Maintains body condition and vitality',
      'Improves feed utilization',
      'Suitable for daily feeding programs',
      'Cost-effective nutritional solution'
    ],
    recommendedFor: 'Dairy cattle, heifers, and growing animals requiring dependable everyday nutrition.',
    image: '/images/products/cattle-feed/z-8000-pro-detail.webp'
  },
  'z-30-pro': {
    id: 'z-30-pro',
    eyebrow: 'Cattle Feed',
    title: 'Z-30',
    tagline: 'Balanced Nutrition. Better Results.',
    subtitle: 'Enhanced Nutrition for Better Results',
    description: [
      'Z-30 is a premium cattle feed designed for farmers seeking improved animal performance. With higher protein and energy levels, it supports growth, productivity, and overall animal health.',
      'It is suitable for productive dairy cattle, growing stock, and animals with higher nutritional requirements in daily feeding programs.'
    ],
    specs: [
      { label: 'Crude Protein', value: '24%' },
      { label: 'Crude Fat', value: '6.0%' },
      { label: 'Crude Fiber', value: '6.5%' }
    ],
    benefits: [
      'Promotes strong growth and development',
      'Provides additional energy for improved performance',
      'Supports milk production and recovery',
      'Helps maintain optimal body condition',
      'Balanced nutrition for healthier animals'
    ],
    recommendedFor: 'Productive dairy cattle, growing stock, and animals with higher nutritional requirements.',
    image: '/images/products/cattle-feed/z-30-pro-detail-new.webp'
  },
  'z-10000': {
    id: 'z-10000',
    eyebrow: 'Cattle Feed',
    title: 'ZFAST 10000',
    tagline: 'Premium Feed. Maximum Productivity.',
    subtitle: 'Premium Nutrition for Maximum Productivity',
    description: [
      'ZFAST 10000 is our flagship cattle feed, developed to deliver superior performance and efficiency. Its high protein content, enhanced energy level, and lower fiber content help support milk production, growth, and feed conversion.'
    ],
    specs: [
      { label: 'Crude Protein', value: '25%' },
      { label: 'Crude Fat', value: '6.5%' },
      { label: 'Crude Fiber', value: '5.0%' }
    ],
    benefits: [
      'High-quality protein for superior growth',
      'Increased energy for peak performance',
      'Supports higher milk yield potential',
      'Improves feed efficiency and nutrient utilization',
      'Helps maintain excellent body condition'
    ],
    recommendedFor: 'High-yield dairy cattle, breeding animals, and commercial dairy operations focused on maximizing productivity.',
    image: '/images/products/cattle-feed/z-10000-pro-detail.webp'
  }
};
